/**
 * Add a borrower — the form, on its own, so two surfaces can render it.
 *
 * Lifted out of `BorrowerList` for DOP-455: the Periods board also offers it,
 * because a new template opens on an empty board and "add one on the Borrowers
 * tab" is an instruction, not an action.
 *
 * Both dropdowns are the SAME control (DOP-469): `BorrowerPicker`, single-answer
 * for the project and multi-answer for the borrowers. The project used to be a
 * native <select>, which put two boxes side by side that looked and behaved
 * nothing alike — different height, different chevron, one searchable and one
 * not — and read as two different kinds of question when they are the same kind
 * asked twice.
 *
 * Two dropdowns, in this order (DOP-459). PROJECT first, because the deal-room
 * folder is what the operator actually knows and it is the only thing that
 * scopes the rest of the form; it is required. BORROWER second and OPTIONAL,
 * filtered to that project — leaving it empty means "every borrower in this
 * project", which is the eight-borrower case that used to be a row of nine
 * "Add all from <folder>" buttons stretched across the form.
 *
 * The borrower field takes SEVERAL names since DOP-468, so the form has three
 * shapes, not two, and the submit button names which one it is on:
 *   0 picked → "Add all N borrowers": the whole project, through the confirm
 *     checklist below, because a project is not a roster.
 *   1 picked → "Add borrower": one create, with the aliases/sponsor/sector this
 *     form only offers for a single name.
 *   2+ picked → "Add N borrowers": those names and no others, so the common
 *     "six of the eight, not the fund's own reporting" needs no checklist — the
 *     picks ARE the list, visible as chips the whole time.
 * The 2+ path writes exactly like the checklist does — one create per name, in
 * order, a refusal not stopping the ones behind it — and reports back the same
 * way: refused names stay picked under an "N of M were not added" line.
 *
 * The name is a PICK, never free text (DOP-454). The package resolver matches a
 * borrower's documents by filename prefix, so a spelling the corpus does not
 * use is a borrower with no package — a failure that shows up two screens later
 * as an empty cell. Hence a picker that filters but never takes what was typed
 * as a value, and no code path that turns it into one.
 *
 * Aliases, sponsor and sector stay free text: they are the operator's own
 * vocabulary and nothing downstream fails on a typo in them (an alias that
 * matches nothing simply matches nothing). They appear only when ONE borrower
 * is picked — they mean nothing spread across a whole project.
 *
 * The bulk add still CONFIRMS before it writes. A project is not a roster: the
 * demo deal room holds eight borrowers plus the fund's own portfolio review, so
 * a one-click add-all created a bogus "Northwood Credit" borrower — and there
 * is no DELETE for a borrower, so it could not be taken back. "Add all N
 * borrowers" opens a checklist of that project's names, all ticked, and the
 * write happens on the second click. There is still no bulk endpoint and it
 * still does not need one: it posts each name in turn and lets the caller's
 * toast report whichever ones the server refused.
 */

import React, { useEffect, useId, useMemo, useState } from "react";
import type {
	MonitoringBorrowerCandidateGroup,
	MonitoringCreateBorrowerPayload,
} from "../../types/monitoring";
import BorrowerPicker from "./BorrowerPicker";

/**
 * What `onCreate` answers with. It reports failure rather than throwing — the
 * page owns the message (and the toast); the form only needs to know whether
 * to clear itself. A rejected promise here used to escape as an unhandled
 * error behind the dev server's red overlay.
 */
export interface BorrowerCreateResult {
	ok: boolean;
}

export interface AddBorrowerFormProps {
	/** The names the caller's documents carry, by folder. */
	candidates: MonitoringBorrowerCandidateGroup[];
	/** Must not reject; a failure comes back as `{ ok: false }`. */
	onCreate: (
		payload: MonitoringCreateBorrowerPayload,
	) => Promise<BorrowerCreateResult>;
	/**
	 * The roster changed — refetch it. Called for EVERY write that landed,
	 * including a bulk add that only PARTLY landed.
	 *
	 * It must not close or unmount this form, and that separation is the whole
	 * reason it is not the same callback as `onDone`: both hosts used to read
	 * "a borrower was added" as "close the form", so a bulk add that was
	 * refused two names out of eight unmounted the form in the middle of
	 * updating it and the retry list it was about to render was never seen. The
	 * user was left with a closed form and six of their eight borrowers, with
	 * nothing on screen saying so.
	 */
	onAdded?: () => void;
	/**
	 * This form is finished; a host may close it. Called only after a single
	 * add succeeds, or a bulk add lands EVERY name it was given.
	 */
	onDone?: () => void;
	onCancel?: () => void;
}

const FIELD =
	"rounded border border-gray-300 px-2 py-1 text-base text-text-base focus:border-[#008782] focus:outline-none";

/**
 * Identifies a project in the <select>. `folder_id` alone will not do: it is
 * null for "Unfiled", and null is not a select value.
 */
const groupKey = (group: MonitoringBorrowerCandidateGroup) =>
	`${group.folder_id ?? "unfiled"}-${group.folder_name}`;

const AddBorrowerForm: React.FC<AddBorrowerFormProps> = ({
	candidates,
	onCreate,
	onAdded,
	onDone,
	onCancel,
}) => {
	//: The chosen project, by `groupKey`. Required — everything below it is
	//  disabled until it holds something.
	const [projectKey, setProjectKey] = useState<string | null>(() =>
		candidates.length === 1 ? groupKey(candidates[0]) : null,
	);
	//: The picked names, in the order they were picked. Empty is not an empty
	//  form — it is "every borrower in this project" (DOP-459).
	const [names, setNames] = useState<string[]>([]);
	const [aliases, setAliases] = useState("");
	const [sponsor, setSponsor] = useState("");
	const [sector, setSector] = useState("");
	const [saving, setSaving] = useState(false);
	const [addingFolder, setAddingFolder] = useState<string | null>(null);
	//: The project whose checklist is open, and which of its names are ticked.
	//  Held as a snapshot of the group: the candidates refresh under us as each
	//  add lands, and a checklist that reshuffled mid-write would be lying.
	const [confirming, setConfirming] =
		useState<MonitoringBorrowerCandidateGroup | null>(null);
	const [checked, setChecked] = useState<ReadonlySet<string>>(() => new Set());
	//: What the last multi-name attempt could not write, whether it came from
	//  the checklist or from a 2+ pick. The refused names stay on screen —
	//  ticked in the checklist, or picked as chips — so the retry is one click
	//  on exactly those, rather than working out which of eight to redo.
	const [failed, setFailed] = useState<{ names: string[]; of: number } | null>(
		null,
	);
	// Generated, not literal: DOP-455 renders this form on the board too, and
	// two `htmlFor="borrower-name"` on one page point at the same field.
	const fieldId = useId();
	const nameId = `${fieldId}-name`;
	const projectId = `${fieldId}-project`;

	// One project is not a choice. The candidates arrive from a fetch, so the
	// single-group case is usually not knowable at mount — hence an effect and
	// not only the initial state. It never overrides a choice already made, and
	// a project that empties out mid-bulk-add keeps its selection rather than
	// snapping elsewhere under the open checklist.
	useEffect(() => {
		if (projectKey !== null || candidates.length !== 1) return;
		setProjectKey(groupKey(candidates[0]));
	}, [candidates, projectKey]);

	const busy = saving || addingFolder !== null;
	const hasCandidates = candidates.some((group) => group.candidates.length > 0);
	const selectedGroup =
		candidates.find((group) => groupKey(group) === projectKey) ?? null;
	// What a bulk add would write: a project's candidates are already the names
	// NOT on the roster — the endpoint filters those out.
	const remaining = selectedGroup?.candidates.length ?? 0;

	// The projects, shaped as one group of options for the same picker the
	// borrower field uses. One group, so it draws no folder heading over its own
	// names, and the count beside each is how many borrowers it still has to
	// offer — which is the number that decides whether a project is worth opening.
	const projectOptions = useMemo(
		() => [
			{
				folder_id: null,
				folder_name: "Projects",
				candidates: candidates.map((group) => ({
					name: group.folder_name,
					file_count: group.candidates.length,
				})),
			},
		],
		[candidates],
	);

	/**
	 * The picker answers with a folder NAME; the form's identity is `groupKey`,
	 * which also distinguishes "Unfiled" (folder_id null) from a real folder.
	 * Two folders sharing a name are already indistinguishable on screen — they
	 * were in the old <select> too — so the first match wins.
	 */
	const chooseProject = (label: string | null) => {
		const next = label
			? (candidates.find((group) => group.folder_name === label) ?? null)
			: null;
		const nextKey = next ? groupKey(next) : null;
		if (nextKey === projectKey) return;
		setProjectKey(nextKey);
		// The picked names belonged to the old project — and so does anything the
		// last attempt could not write.
		setNames([]);
		setFailed(null);
	};

	/** The caller's contract says this never rejects; the catch is the belt. */
	const create = (payload: MonitoringCreateBorrowerPayload) =>
		onCreate(payload).catch(() => ({ ok: false }) as BorrowerCreateResult);

	const closeChecklist = () => {
		setConfirming(null);
		setChecked(new Set());
		setFailed(null);
	};

	const openChecklist = (group: MonitoringBorrowerCandidateGroup) => {
		if (busy || group.candidates.length === 0) return;
		setConfirming(group);
		setFailed(null);
		// Ticked by default: adding the whole project is the common case, and
		// the one name to untick (the fund itself) is easier to spot than eight
		// to tick.
		setChecked(new Set(group.candidates.map((candidate) => candidate.name)));
	};

	const clearForm = () => {
		setNames([]);
		setAliases("");
		setSponsor("");
		setSector("");
		setFailed(null);
	};

	/**
	 * Posts a list of names, one at a time and in order. There is no bulk
	 * endpoint and this still does not need one. A refused name does not stop
	 * the ones behind it — and it is not swallowed either: the form used to
	 * close on any outcome, so a click that wrote six of eight borrowers looked
	 * exactly like one that wrote eight. Answers with the names refused, having
	 * already told the host about whatever landed.
	 */
	const createEach = async (batch: string[]) => {
		const refused: string[] = [];
		for (const name of batch) {
			const result = await create({
				name,
				aliases: [],
				sponsor: null,
				sector: null,
			});
			if (!result.ok) refused.push(name);
		}
		// Whatever DID land has to reach the host: the roster and the board are
		// both stale now, even on a partial write. This is a refresh and nothing
		// else — the form stays mounted, because on a partial write it is about
		// to render the retry list.
		if (refused.length < batch.length) onAdded?.();
		return refused;
	};

	const submit = async (event: React.FormEvent) => {
		event.preventDefault();
		// The button is disabled while saving, but a form submits on Enter too.
		if (busy || !selectedGroup) return;
		// No borrower picked is not an incomplete form — it is the bulk add, and
		// it goes through the same confirm checklist it always did.
		if (names.length === 0) {
			openChecklist(selectedGroup);
			return;
		}
		// A retry starts clean: the line below the button describes THIS attempt.
		setFailed(null);
		setSaving(true);
		try {
			if (names.length > 1) {
				// Several picks are their own confirmation — they are chips the
				// user put there one by one and can see while pressing the button —
				// so this writes them without a second checklist.
				const refused = await createEach(names);
				if (refused.length === 0) {
					clearForm();
					// Every name landed: nothing left to show, so a host may close.
					onDone?.();
					return;
				}
				// Narrowed to what failed, still picked, so the same button retries
				// exactly those.
				setNames(refused);
				setFailed({ names: refused, of: names.length });
				return;
			}
			const result = await create({
				name: names[0],
				aliases: aliases
					.split(",")
					.map((alias) => alias.trim())
					.filter(Boolean),
				// Empty optional fields go as null, not "": the contract types them
				// nullable, and an empty string would render as a blank sponsor
				// rather than as no sponsor.
				sponsor: sponsor.trim() || null,
				sector: sector.trim() || null,
			});
			// Kept as typed on failure: the write did not happen, and clearing the
			// form would make the user enter it again.
			if (!result.ok) return;
			clearForm();
			// One name, and it landed: the roster changed AND this form is done.
			onAdded?.();
			onDone?.();
		} finally {
			setSaving(false);
		}
	};

	const toggle = (name: string) =>
		setChecked((current) => {
			const next = new Set(current);
			if (!next.delete(name)) next.add(name);
			return next;
		});

	const addChecked = async () => {
		if (busy || !confirming) return;
		const batch = confirming.candidates
			.map((candidate) => candidate.name)
			.filter((name) => checked.has(name));
		if (batch.length === 0) return;
		setAddingFolder(confirming.folder_name);
		try {
			// Over the snapshot taken when the checklist opened: the candidates
			// refresh as each add lands, and iterating the live array would skip
			// names as it shortened.
			const refused = await createEach(batch);
			if (refused.length === 0) {
				closeChecklist();
				// Every name landed: nothing left to show, so a host may close.
				onDone?.();
				return;
			}
			// Narrow the checklist to what failed, still ticked, so the same
			// button retries exactly those.
			setConfirming({
				...confirming,
				candidates: confirming.candidates.filter((candidate) =>
					refused.includes(candidate.name),
				),
			});
			setChecked(new Set(refused));
			setFailed({ names: refused, of: batch.length });
		} finally {
			setAddingFolder(null);
		}
	};

	// Three shapes, and the button is the only thing on screen that says which
	// one the form is on: one pick writes one borrower, several write exactly
	// those, none writes the project (through the checklist).
	const submitLabel = () => {
		if (saving) return "Adding…";
		if (names.length > 1) return `Add ${names.length} borrowers`;
		if (!selectedGroup || names.length === 1) return "Add borrower";
		return `Add all ${remaining} ${remaining === 1 ? "borrower" : "borrowers"}`;
	};

	return (
		<form
			onSubmit={submit}
			className="rounded-xl border border-gray-200 bg-white p-4"
		>
			{hasCandidates ? (
				<>
					<div className="grid grid-cols-2 gap-3">
						<div className="flex flex-col gap-1">
							<label className="text-sm text-text-muted" htmlFor={projectId}>
								Project
							</label>
							<BorrowerPicker
								inputId={projectId}
								mode="single"
								groups={projectOptions}
								values={selectedGroup ? [selectedGroup.folder_name] : []}
								onChange={(picked) => chooseProject(picked[0] ?? null)}
								// Not while a checklist is up: that list is a snapshot of one
								// project, and switching underneath it would leave the confirm
								// describing a project the form is no longer on.
								disabled={busy || confirming !== null}
								placeholder="Select a project"
								countLabel={(count) =>
									`${count} ${count === 1 ? "borrower" : "borrowers"}`
								}
								emptyLabel="No project matches."
								listLabel="Projects"
							/>
							<span className="text-xs text-text-muted">
								The deal-room folder these documents were filed in.
							</span>
						</div>
						<div className="flex flex-col gap-1">
							{/* The hint sits OUTSIDE the label: a label's text is the
							    field's accessible name, and "Borrower Optional — leave it
							    empty…" is not what a screen reader should read out as the
							    name of this box. */}
							<label className="text-sm text-text-muted" htmlFor={nameId}>
								Borrower
							</label>
							<BorrowerPicker
								inputId={nameId}
								groups={selectedGroup ? [selectedGroup] : []}
								values={names}
								onChange={setNames}
								disabled={busy || !selectedGroup}
								placeholder="All borrowers in this project"
							/>
							<span className="text-xs text-text-muted">
								Pick as many as you like — or leave it empty to add every
								borrower in the project. Only names your documents already use:
								the package matcher reads the filename.
							</span>
						</div>
						{/* Only for a single borrower. Spread across several they would be
						    one sponsor and one sector asserted about companies that share
						    neither. */}
						{names.length === 1 && (
							<>
								<label className="flex flex-col gap-1">
									<span className="text-sm text-text-muted">Aliases</span>
									<input
										type="text"
										value={aliases}
										onChange={(event) => setAliases(event.target.value)}
										placeholder="Apex Mfg, Apex Manufacturing Inc"
										className={FIELD}
									/>
									<span className="text-xs text-text-muted">
										Comma-separated. Used to match uploaded files to this
										borrower.
									</span>
								</label>
								<label className="flex flex-col gap-1">
									<span className="text-sm text-text-muted">Sponsor</span>
									<input
										type="text"
										value={sponsor}
										onChange={(event) => setSponsor(event.target.value)}
										className={FIELD}
									/>
								</label>
								<label className="flex flex-col gap-1">
									<span className="text-sm text-text-muted">Sector</span>
									<input
										type="text"
										value={sector}
										onChange={(event) => setSector(event.target.value)}
										className={FIELD}
									/>
								</label>
							</>
						)}
					</div>

					{selectedGroup && remaining === 0 && (
						<p className="mt-3 text-sm text-text-muted">
							Every borrower in this project is on the roster.
						</p>
					)}

					{confirming ? (
						<div className="mt-3 border-t border-gray-100 pt-3">
							<p className="text-sm text-text-base">
								Add these borrowers from {confirming.folder_name}
							</p>
							{failed ? (
								<p className="mt-1 rounded border border-[#fecaca] bg-[#fef2f2] px-2 py-1.5 text-sm text-[#991b1b]">
									{failed.names.length} of {failed.of} were not added:{" "}
									{failed.names.join(", ")}
								</p>
							) : (
								<p className="mt-0.5 text-xs text-text-muted">
									Untick anything that is not a borrower — a fund's own reporting
									is filed here too, and a borrower cannot be deleted once added.
								</p>
							)}
							<ul className="mt-2 max-h-48 space-y-1 overflow-y-auto">
								{confirming.candidates.map((candidate) => (
									<li key={candidate.name}>
										<label className="flex items-center gap-2 text-base text-text-base">
											<input
												type="checkbox"
												checked={checked.has(candidate.name)}
												onChange={() => toggle(candidate.name)}
												disabled={busy}
												className="h-3.5 w-3.5 accent-[#008782]"
											/>
											{candidate.name}
											<span className="text-sm text-text-muted">
												{candidate.file_count}{" "}
												{candidate.file_count === 1 ? "file" : "files"}
											</span>
										</label>
									</li>
								))}
							</ul>
							<div className="mt-3 flex flex-wrap items-center gap-3">
								<button
									type="button"
									onClick={() => void addChecked()}
									disabled={busy || checked.size === 0}
									className="rounded bg-[#008782] px-3 py-1.5 text-sm font-medium text-white hover:bg-[#004C4B] disabled:opacity-60"
								>
									{addingFolder
										? `Adding ${checked.size}…`
										: `Add ${checked.size} ${
												checked.size === 1 ? "borrower" : "borrowers"
											}`}
								</button>
								<button
									type="button"
									onClick={closeChecklist}
									disabled={busy}
									className="rounded border border-gray-300 px-3 py-1.5 text-sm font-medium text-text-base hover:border-gray-400 disabled:opacity-60"
								>
									Cancel
								</button>
							</div>
						</div>
					) : (
						// Hidden while the checklist is up: the confirm owns the primary
						// action then, and two "Add …" buttons on one form is a coin toss.
						<>
							{failed && (
								// The same report the checklist gives, for the same reason:
								// several picks that only partly landed must not look like
								// several that all did. The refused names are still in the
								// picker above, so the button below retries exactly them.
								<p className="mt-3 rounded border border-[#fecaca] bg-[#fef2f2] px-2 py-1.5 text-sm text-[#991b1b]">
									{failed.names.length} of {failed.of} were not added:{" "}
									{failed.names.join(", ")}
								</p>
							)}
							<div className="mt-3 flex flex-wrap items-center gap-3">
								<button
									type="submit"
									disabled={busy || !selectedGroup || remaining === 0}
									className="rounded bg-[#008782] px-3 py-1.5 text-sm font-medium text-white hover:bg-[#004C4B] disabled:opacity-60"
								>
									{submitLabel()}
								</button>
								{onCancel && (
									<button
										type="button"
										onClick={onCancel}
										className="rounded border border-gray-300 px-3 py-1.5 text-sm font-medium text-text-base hover:border-gray-400"
									>
										Cancel
									</button>
								)}
							</div>
						</>
					)}
				</>
			) : (
				<div>
					<p className="text-base text-text-base">
						No borrower documents found in your files.
					</p>
					<p className="mt-1 text-sm text-text-muted">
						Upload the deal-room documents first — a borrower is named by the text
						before the first " - " in a filename, like "Apex Manufacturing - Q4
						2025 Compliance Certificate.pdf".
					</p>
					{onCancel && (
						<button
							type="button"
							onClick={onCancel}
							className="mt-3 rounded border border-gray-300 px-3 py-1.5 text-sm font-medium text-text-base hover:border-gray-400"
						>
							Cancel
						</button>
					)}
				</div>
			)}
		</form>
	);
};

export default AddBorrowerForm;
